<template>
  <div>
    <h2 class="bank-header mb-3">
      Bank Statement: <span v-if="isLoading">
        <b-spinner small label="Loading..." class="ml-2"></b-spinner>
      </span>
      <span v-else class="bank-name">
        {{ bankStatementName }}
      </span>
    </h2>
    <b-table 
      :items="transactions" 
      :fields="fields" 
      bordered 
      responsive="sm"
      :per-page="perPage"
      :current-page="currentPage"
    >
      <template #cell(reconciled)="data">
        <span v-if="data.item.reconciled" class="badge badge-success">Reconciled</span>
        <b-form-checkbox
          v-else
          :checked="data.item.reconciled"
          switch
          @change="toggleRecognized(data.item.transactionId)"
        ></b-form-checkbox>
      </template>
      <template #cell(date)="data">
        {{ formatDate(data.item.date) }}
      </template>
    </b-table>
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div class="flex-grow-1 d-flex justify-content-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="transactions.length"
          :per-page="perPage"
        ></b-pagination>
      </div>
      <b-form-group label="Rows per page:" class="mb-0 ml-3">
        <b-form-select v-model="perPage" :options="perPageOptions"></b-form-select>
      </b-form-group>
    </div>
    
  </div>
</template>

<script>
import { BTable, BFormCheckbox, BPagination, BFormSelect, BFormGroup, BSpinner } from "bootstrap-vue";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
export default {
  components: {
    BTable,
    BFormCheckbox,
    BPagination,
    BFormSelect,
    BFormGroup,
    BSpinner
  },
  data() {
    return {
      statementID: this.$route.params.id,
      bankStatementName: "",
      isLoading: true,
      fields: [
        { key: "line", label: "Line" },
        { key: "code", label: "Code" },
        { key: "description", label: "Description" },
        { key: "reference", label: "Reference" },
        { key: "date", label: "Date", sortable: true },
        { key: "debit", label: "Debit", sortable: true },
        { key: "credit", label: "Credit", sortable: true },
        { key: "reconciled", label: "Reconciled", sortable: false },
      ],
      transactions: [],
      perPage: 5, 
      currentPage: 1,
      perPageOptions: [5, 10, 20, 50], 
    };
  },
  methods: {
    formatDate(date) {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString("en-US", options);
    },
    ...mapActions("bankStatementModule", ["getUploadBankStatementByIdAction", "putUploadBankStatementByIdAction"]),
    
    async toggleRecognized(transactionId) {   
    try {
        const transaction = this.transactions.find(t => t.transactionId === transactionId);
        if (!transaction) {
            console.warn("Transaction not found:", transactionId);
            return;
        }

        // Confirm before reconciling
        const confirmResult = await Swal.fire({
            title: "Confirm Reconciliation",
            text: "Are you sure you want to reconcile this transaction?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, reconcile",
            cancelButtonText: "Cancel",
            customClass: {
                confirmButton: "btn btn-success mx-2",
                cancelButton: "btn btn-secondary mx-2"
            },
            buttonsStyling: false
        });

        // If user cancels, do nothing
        if (!confirmResult.isConfirmed) {
            transaction.reconciled = false; // Revert checkbox
            return;
        }

        // Show loading message
        Swal.fire({
            title: "Reconciling...",
            text: "Updating reconciliation status, please wait...",
            didOpen: () => {
                Swal.showLoading();
            },
            allowOutsideClick: false,
        });

        // Perform API call
        const response = await this.putUploadBankStatementByIdAction({
          bankStatementLineID: transactionId,
            status: "RECONCILED"
        });

        if (response && response.success) {
            // Update local transaction state after a successful API call
            transaction.reconciled = true;

            // Show success message
            Swal.fire({
                title: "Reconciled!",
                text: "Transaction successfully reconciled.",
                icon: "success",
                confirmButtonText: "OK"
            });
        } else {
            throw new Error("API call did not return success");
        }
    } catch (error) {
        console.error("Failed to update reconciliation status", error);
        
        // Show error message
        Swal.fire({
            title: "Error!",
            text: "Failed to update reconciliation status.",
            icon: "error",
            confirmButtonText: "OK"
        });

        // Revert checkbox back to its previous state
        const transaction = this.transactions.find(t => t.transactionId === transactionId);
        if (transaction) {
            transaction.reconciled = false;
        }
    }
},  },
  async mounted() {
    try {
      this.$swal({
        title: "Fetching data...",
        text: "Please wait while we load the reconciliations.",
        icon: "info",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      const response = await this.getUploadBankStatementByIdAction(this.statementID);
      console.log("bank details:", response);

      if(response && response.result){
        this.bankStatementName = response.result.bankStatement?.name || "Unknown Statement";
        if (response && response.result && response.result.bankStatementLines) {
        
        this.transactions = response.result.bankStatementLines.map(item => ({
          transactionId: item.id,
          line: item.line,
          code: item.code,
          date: item.date || "-",
          description: item.description,
          reference: item.reference,
          credit: parseFloat(item.credit).toFixed(2),
          debit: parseFloat(item.debit).toFixed(2),
          reconciled: item.status !== null && item.status !== "PENDING", // Ensure boolean
        }));
      } else {
        console.warn("No data received", response);
        this.transactions = [];
      }
      }
    } catch (e) {
      console.error("Error fetching reconciliation", e);
      this.$swal({
        title: "Error!",
        text: "Failed to fetch data.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      this.$swal.close(); 
      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
/* Add styling for better alignment */
.d-flex {
  display: flex;
}
.justify-content-between {
  justify-content: space-between;
}
.align-items-center {
  align-items: center;
}
.mt-3 {
  margin-top: 1rem;
}
.mb-0 {
  margin-bottom: 0;
}
</style>
